import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDocument, updateDocument } from "../config";
import moment from "moment";

const DEFAULT_WIDGET_IDS = ["dw0", "dw1", "dw2", "dw3", "dw4", "dw5", "dw6", "dw7", "dw8"];

const DEFAULT_WIDGET_TYPES = {
  dw0: { dataType: "donationActionItems",      timeRange: null },
  dw1: { dataType: "donationSummaryStats",     timeRange: "Trailing 12 Months" },
  dw2: { dataType: "donationTopLocations",     timeRange: null },
  dw3: { dataType: "donationBottomLocations",  timeRange: null },
  dw4: { dataType: "donationTopOrganizations", timeRange: null },
  dw5: { dataType: "donationAggregateValue",   timeRange: null },
  dw6: { dataType: "donationRequestChart",     timeRange: null },
  dw7: { dataType: "donationTypes",            timeRange: "Trailing 12 Months" },
  dw8: { dataType: "donationLocationTable",    timeRange: null },
};
export const MAX_DONATION_WIDGETS = 18;

const makeMap = (ids, val) =>
  ids.reduce((acc, id) => ({ ...acc, [id]: val }), {});

/** Convert a quickDates label to a { start, end } moment range. */
const getDateRangeFromLabel = (label) => {
  const now = moment();
  switch (label) {
    case "This Week":
      return { start: now.clone().startOf("week"), end: now.clone().endOf("week") };
    case "This Month":
      return { start: now.clone().startOf("month"), end: now.clone().endOf("month") };
    case "This Quarter":
      return { start: now.clone().startOf("quarter"), end: now.clone().endOf("quarter") };
    case "This Year":
      return { start: now.clone().startOf("year"), end: now.clone().endOf("year") };
    case "Last Week":
      return { start: now.clone().subtract(1, "week").startOf("week"), end: now.clone().subtract(1, "week").endOf("week") };
    case "Last Month":
      return { start: now.clone().subtract(1, "month").startOf("month"), end: now.clone().subtract(1, "month").endOf("month") };
    case "Last Quarter":
      return { start: now.clone().subtract(1, "quarter").startOf("quarter"), end: now.clone().subtract(1, "quarter").endOf("quarter") };
    case "Last Year":
      return { start: now.clone().subtract(1, "year").startOf("year"), end: now.clone().subtract(1, "year").endOf("year") };
    case "Trailing 12 Months":
      return { start: moment().subtract(11, "months").startOf("month"), end: moment().endOf("month") };
    case "Since Inception":
    default:
      return { start: moment("2010-01-01"), end: moment().endOf("day") };
  }
};

/** Filter monthlyData to entries within the given moment range. */
const filterMonthlyData = (monthlyData, range) => {
  if (!monthlyData || !monthlyData.length) return [];
  return monthlyData.filter((m) => {
    const monthStart = moment(m.month, "YYYY-MM").startOf("month");
    return monthStart.isSameOrAfter(range.start, "month") && monthStart.isSameOrBefore(range.end, "month");
  });
};

// ---------- thunks ----------

export const loadDonationWidgetConfig = createAsyncThunk(
  "donationWidgets/loadConfig",
  async (_, { getState, rejectWithValue }) => {
    const { account } = getState();
    const uid = account.user?.id;
    if (!uid) return rejectWithValue("No user ID");
    try {
      const userDoc = await getDocument("users", uid);
      return {
        widgetConfig: userDoc?.donationWidgetConfig || {},
        widgetIds: userDoc?.donationWidgetIds || null,
      };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const saveDonationWidgetConfig = createAsyncThunk(
  "donationWidgets/saveConfig",
  async ({ widgetId, dataType, timeRange }, { getState, rejectWithValue }) => {
    const { account, donationWidgets } = getState();
    const uid = account.user?.id;
    if (!uid) return rejectWithValue("No user ID");
    try {
      const newConfig = {
        ...donationWidgets.config,
        [widgetId]: { dataType: dataType || null, timeRange: timeRange || null },
      };
      await updateDocument({ entity: "users", id: uid }, { donationWidgetConfig: newConfig });
      return { widgetId, dataType: dataType || null, timeRange: timeRange || null };
    } catch (e) {
      return rejectWithValue({ widgetId, error: e.message });
    }
  }
);

export const saveDonationWidgetIds = createAsyncThunk(
  "donationWidgets/saveIds",
  async (_, { getState, rejectWithValue }) => {
    const { account, donationWidgets } = getState();
    const uid = account.user?.id;
    if (!uid) return rejectWithValue("No user ID");
    try {
      await updateDocument({ entity: "users", id: uid }, { donationWidgetIds: donationWidgets.widgetIds });
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const removeDonationWidgetAndPersist = createAsyncThunk(
  "donationWidgets/remove",
  async (widgetId, { getState, rejectWithValue }) => {
    const { account, donationWidgets } = getState();
    const uid = account.user?.id;
    if (!uid) return rejectWithValue("No user ID");
    try {
      const nextIds = donationWidgets.widgetIds.filter((id) => id !== widgetId);
      const nextConfig = { ...donationWidgets.config };
      delete nextConfig[widgetId];
      await updateDocument(
        { entity: "users", id: uid },
        { donationWidgetIds: nextIds, donationWidgetConfig: nextConfig }
      );
      return { widgetId };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchDonationWidgetData = createAsyncThunk(
  "donationWidgets/fetchData",
  async (widgetId, { getState, rejectWithValue }) => {
    const { donationWidgets, donationDashboard } = getState();
    const cfg = donationWidgets.config[widgetId];
    if (!cfg || !cfg.dataType) return { widgetId, data: null };

    const { dataType, timeRange } = cfg;

    try {
      let data;

      switch (dataType) {
        case "donationActionItems": {
          data = {
            pendingApproval: donationDashboard.pendingApproval,
            reviewing: donationDashboard.reviewing,
            approved: donationDashboard.approved,
            denied: donationDashboard.denied,
            fulfilled: donationDashboard.requestsFulfilled,
          };
          break;
        }

        case "donationSummaryStats": {
          const range = getDateRangeFromLabel(timeRange || "Trailing 12 Months");
          const filtered = filterMonthlyData(donationDashboard.monthlyData, range);
          const totalReqs = filtered.reduce((s, m) => s + (m.requests || 0), 0);
          const totalApproved = filtered.reduce((s, m) => s + (m.approved || 0), 0);
          const totalDenied = filtered.reduce((s, m) => s + (m.denied || 0), 0);
          const totalVal = filtered.reduce((s, m) => s + (m.value || 0), 0);
          const approvedCnt = filtered.reduce((s, m) => s + (m.approved || 0), 0);
          const avgVal = approvedCnt > 0 ? totalVal / approvedCnt : 0;
          const acceptedPct = totalReqs > 0 ? (totalApproved / totalReqs) * 100 : 0;
          const deniedPct = totalReqs > 0 ? (totalDenied / totalReqs) * 100 : 0;
          data = {
            totalRequests: totalReqs || donationDashboard.totalRequests,
            acceptedPct,
            deniedPct,
            avgValue: avgVal || donationDashboard.avgDonation,
            totalValue: totalVal || donationDashboard.totalDonationValue,
            usingAllTime: filtered.length === 0,
          };
          // Fall back to all-time totals when no monthly data is available
          if (filtered.length === 0) {
            data = {
              totalRequests: donationDashboard.totalRequests,
              acceptedPct: donationDashboard.totalRequests > 0
                ? (donationDashboard.approved / donationDashboard.totalRequests) * 100 : 0,
              deniedPct: donationDashboard.totalRequests > 0
                ? (donationDashboard.denied / donationDashboard.totalRequests) * 100 : 0,
              avgValue: donationDashboard.avgDonation,
              totalValue: donationDashboard.totalDonationValue,
              usingAllTime: true,
            };
          }
          break;
        }

        case "donationTopLocations": {
          if (timeRange && timeRange !== "Trailing 12 Months" && timeRange !== "Since Inception") {
            const range = getDateRangeFromLabel(timeRange);
            const locationMonthly = donationDashboard.locationMonthlyData || {};
            const filteredMonths = new Set(
              filterMonthlyData(donationDashboard.monthlyData || [], range).map((m) => m.month)
            );
            data = Object.entries(locationMonthly)
              .map(([name, months]) => ({
                name,
                value: Object.entries(months)
                  .filter(([mk]) => filteredMonths.has(mk))
                  .reduce((s, [, v]) => s + v, 0),
              }))
              .sort((a, b) => b.value - a.value)
              .slice(0, 10);
          } else {
            data = [...(donationDashboard.topLocations || [])].slice(0, 10);
          }
          break;
        }

        case "donationBottomLocations": {
          if (timeRange && timeRange !== "Trailing 12 Months" && timeRange !== "Since Inception") {
            const range = getDateRangeFromLabel(timeRange);
            const locationMonthly = donationDashboard.locationMonthlyData || {};
            const filteredMonths = new Set(
              filterMonthlyData(donationDashboard.monthlyData || [], range).map((m) => m.month)
            );
            data = Object.entries(locationMonthly)
              .map(([name, months]) => ({
                name,
                value: Object.entries(months)
                  .filter(([mk]) => filteredMonths.has(mk))
                  .reduce((s, [, v]) => s + v, 0),
              }))
              .filter((l) => l.value > 0)
              .sort((a, b) => a.value - b.value)
              .slice(0, 10);
          } else {
            data = [...(donationDashboard.bottomLocations || [])].slice(0, 10);
          }
          break;
        }

        case "donationTopOrganizations": {
          if (timeRange && timeRange !== "Trailing 12 Months" && timeRange !== "Since Inception") {
            const range = getDateRangeFromLabel(timeRange);
            const orgMonthly = donationDashboard.orgMonthlyData || {};
            const filteredMonths = new Set(
              filterMonthlyData(donationDashboard.monthlyData || [], range).map((m) => m.month)
            );
            data = Object.entries(orgMonthly)
              .map(([name, months]) => ({
                name,
                count: Object.entries(months)
                  .filter(([mk]) => filteredMonths.has(mk))
                  .reduce((s, [, v]) => s + (v.count || 0), 0),
                value: Object.entries(months)
                  .filter(([mk]) => filteredMonths.has(mk))
                  .reduce((s, [, v]) => s + (v.value || 0), 0),
              }))
              .filter((o) => o.value > 0)
              .sort((a, b) => b.value - a.value)
              .slice(0, 10);
          } else {
            data = [...(donationDashboard.topOrganizations || [])].slice(0, 10);
          }
          break;
        }

        case "donationAggregateValue": {
          const monthly = donationDashboard.monthlyData || [];
          const range = timeRange ? getDateRangeFromLabel(timeRange) : null;
          const filtered = range ? filterMonthlyData(monthly, range) : monthly;
          data = {
            labels: filtered.map((m) => moment(m.month, "YYYY-MM").format("MMM 'YY")),
            datasets: [
              {
                label: "Donation Value ($)",
                backgroundColor: "#00d78b",
                borderColor: "#00d78b",
                data: filtered.map((m) => parseFloat((m.value || 0).toFixed(2))),
              },
            ],
          };
          break;
        }

        case "donationRequestChart": {
          const monthly = donationDashboard.monthlyData || [];
          const range = timeRange ? getDateRangeFromLabel(timeRange) : null;
          const filtered = range ? filterMonthlyData(monthly, range) : monthly;
          data = {
            labels: filtered.map((m) => moment(m.month, "YYYY-MM").format("MMM 'YY")),
            datasets: [
              {
                label: "Requests",
                backgroundColor: "#00a2ca",
                borderColor: "#00a2ca",
                data: filtered.map((m) => m.requests || 0),
              },
              {
                label: "Approved",
                backgroundColor: "#00d78b",
                borderColor: "#00d78b",
                data: filtered.map((m) => m.approved || 0),
              },
              {
                label: "Denied",
                backgroundColor: "#FF1E00",
                borderColor: "#FF1E00",
                data: filtered.map((m) => m.denied || 0),
              },
            ],
          };
          break;
        }

        case "donationTypes": {
          const range = getDateRangeFromLabel(timeRange || "Trailing 12 Months");
          const typeMonthly = donationDashboard.donationTypesMonthly || {};
          const monthlyData = donationDashboard.monthlyData || [];
          const filteredMonths = filterMonthlyData(monthlyData, range).map((m) => m.month);
          const monthSet = new Set(filteredMonths);

          if (filteredMonths.length === 0) {
            // Fall back to all-time byType data
            data = (donationDashboard.byType || []).slice(0, 10).map((t) => ({
              name: t.name,
              requests: t.count,
              denials: 0,
              approvals: 0,
              value: t.value,
            }));
          } else {
            const typeAgg = {};
            Object.entries(typeMonthly).forEach(([typeName, months]) => {
              Object.entries(months).forEach(([month, stats]) => {
                if (!monthSet.has(month)) return;
                if (!typeAgg[typeName]) typeAgg[typeName] = { requests: 0, denials: 0, approvals: 0, value: 0 };
                typeAgg[typeName].requests += stats.requests || 0;
                typeAgg[typeName].denials += stats.denials || 0;
                typeAgg[typeName].approvals += stats.approvals || 0;
                typeAgg[typeName].value += stats.value || 0;
              });
            });
            data = Object.entries(typeAgg)
              .sort((a, b) => b[1].requests - a[1].requests)
              .map(([name, stats]) => ({ name, ...stats }));
          }
          break;
        }

        case "donationLocationTable": {
          const monthKeys = donationDashboard.monthKeys || [];
          const locationMonthly = donationDashboard.locationMonthlyData || {};
          // Build sorted list of location names by total value
          const locTotals = Object.entries(locationMonthly).map(([loc, months]) => ({
            name: loc,
            total: Object.values(months).reduce((s, v) => s + v, 0),
          }));
          locTotals.sort((a, b) => b.total - a.total);
          data = {
            monthKeys,
            locations: locTotals.map(({ name }) => ({
              name,
              months: monthKeys.map((mk) => locationMonthly[name]?.[mk] || 0),
            })),
          };
          break;
        }

        default:
          data = null;
      }

      return { widgetId, data };
    } catch (e) {
      return rejectWithValue({ widgetId, error: e.message });
    }
  }
);

// ---------- slice ----------

export const donationWidgetsSlice = createSlice({
  name: "donationWidgets",
  initialState: {
    widgetIds: [...DEFAULT_WIDGET_IDS],
    config: DEFAULT_WIDGET_IDS.reduce((acc, id) => ({ ...acc, [id]: DEFAULT_WIDGET_TYPES[id] || { dataType: null, timeRange: null } }), {}),
    configLoading: false,
    configError: null,
    data: makeMap(DEFAULT_WIDGET_IDS, null),
    loading: makeMap(DEFAULT_WIDGET_IDS, false),
    errors: makeMap(DEFAULT_WIDGET_IDS, null),
  },
  reducers: {
    setDonationWidgetConfig: (state, action) => {
      const { widgetId, dataType, timeRange } = action.payload;
      state.config[widgetId] = { dataType: dataType || null, timeRange: timeRange || null };
    },
    clearDonationWidgetData: (state, action) => {
      const widgetId = action.payload;
      state.data[widgetId] = null;
      state.errors[widgetId] = null;
    },
    reorderDonationWidgets: (state, action) => {
      const { oldIndex, newIndex } = action.payload;
      const [removed] = state.widgetIds.splice(oldIndex, 1);
      state.widgetIds.splice(newIndex, 0, removed);
    },
    addDonationWidgetSlot: (state) => {
      if (state.widgetIds.length >= MAX_DONATION_WIDGETS) return;
      const maxNum = state.widgetIds.reduce((max, id) => {
        const m = /^dw(\d+)$/.exec(String(id));
        return m ? Math.max(max, parseInt(m[1], 10)) : max;
      }, -1);
      const nextId = `dw${maxNum + 1}`;
      state.widgetIds.push(nextId);
      state.config[nextId] = { dataType: null, timeRange: null };
      state.data[nextId] = null;
      state.loading[nextId] = false;
      state.errors[nextId] = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDonationWidgetConfig.pending, (state) => {
        state.configLoading = true;
        state.configError = null;
      })
      .addCase(loadDonationWidgetConfig.fulfilled, (state, action) => {
        state.configLoading = false;
        const { widgetConfig, widgetIds } = action.payload;
        const hasPersistedConfig = Object.keys(widgetConfig).length > 0;
        const ids = widgetIds && widgetIds.length >= DEFAULT_WIDGET_IDS.length
          ? widgetIds
          : DEFAULT_WIDGET_IDS;
        state.widgetIds = ids;
        ids.forEach((id) => {
          // Apply persisted config if it exists, otherwise use sensible defaults
          state.config[id] = widgetConfig[id] || (hasPersistedConfig ? { dataType: null, timeRange: null } : (DEFAULT_WIDGET_TYPES[id] || { dataType: null, timeRange: null }));
          if (state.data[id] === undefined) state.data[id] = null;
          if (state.loading[id] === undefined) state.loading[id] = false;
          if (state.errors[id] === undefined) state.errors[id] = null;
        });
      })
      .addCase(loadDonationWidgetConfig.rejected, (state, action) => {
        state.configLoading = false;
        state.configError = action.payload;
      });

    builder.addCase(removeDonationWidgetAndPersist.fulfilled, (state, action) => {
      const { widgetId } = action.payload;
      state.widgetIds = state.widgetIds.filter((id) => id !== widgetId);
      delete state.config[widgetId];
      delete state.data[widgetId];
      delete state.loading[widgetId];
      delete state.errors[widgetId];
    });

    builder
      .addCase(saveDonationWidgetConfig.fulfilled, (state, action) => {
        const { widgetId, dataType, timeRange } = action.payload;
        state.config[widgetId] = { dataType, timeRange };
      })
      .addCase(saveDonationWidgetConfig.rejected, (state, action) => {
        if (action.payload?.widgetId) {
          state.errors[action.payload.widgetId] = action.payload.error;
        }
      });

    builder
      .addCase(fetchDonationWidgetData.pending, (state, action) => {
        const widgetId = action.meta.arg;
        state.loading[widgetId] = true;
        state.errors[widgetId] = null;
      })
      .addCase(fetchDonationWidgetData.fulfilled, (state, action) => {
        if (!action.payload) return;
        const { widgetId, data } = action.payload;
        state.loading[widgetId] = false;
        state.data[widgetId] = data;
      })
      .addCase(fetchDonationWidgetData.rejected, (state, action) => {
        const payload = action.payload || {};
        const widgetId = payload.widgetId || action.meta.arg;
        state.loading[widgetId] = false;
        state.errors[widgetId] = payload.error || "Failed to load widget data.";
      });
  },
});

export const {
  setDonationWidgetConfig,
  clearDonationWidgetData,
  reorderDonationWidgets,
  addDonationWidgetSlot,
} = donationWidgetsSlice.actions;

export default donationWidgetsSlice.reducer;
